'use client';

import React, { useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Servicio para exportadores: importador de registro en EE.UU.
 * Bloque aparte, recuadrado en dorado y de otro color, después de los servicios
 * de comercio (mismo criterio que la pág. 3 del catálogo Rev. 5).
 * Texto = texto maestro aprobado por José el 9-oct-2026; no agregar FSVP ni
 * «verificamos al proveedor» hasta tener qualified individual. Sin tarifas.
 */

const GOLD = '#C9A84C';
const GOLD_TEXT = '#E3C878';

const DOCS = {
  es: '/docs/Mercalia-Importador-de-registro-EEUU-ES.pdf',
  en: '/docs/Mercalia-Importer-of-Record-US-EN.pdf',
  catalog: '/docs/Mercalia-Product-Catalog-Rev6-EN-ES.pdf',
};

function DownloadLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200 hover:bg-white/10"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)' }}
    >
      <svg width="18" height="18" fill="none" stroke={GOLD_TEXT} strokeWidth="1.7" viewBox="0 0 24 24" className="shrink-0">
        <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
      </svg>
      {label}
    </a>
  );
}

export default function ExporterService() {
  const { t, lang } = useLanguage();

  // Desde la portada se llega con /services#importador-de-registro: el export
  // estático no baja solo al ancla al hidratar, así que se fuerza el desplazamiento.
  useEffect(() => {
    if (window.location.hash !== '#importador-de-registro') return;
    const timer = setTimeout(() => {
      document.getElementById('importador-de-registro')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(timer);
  }, []);

  const sheet = lang === 'en' ? DOCS.en : DOCS.es;
  const sheetAlt = lang === 'en' ? DOCS.es : DOCS.en;
  const mailto = `mailto:contact@mercaliacg.com?subject=${encodeURIComponent(t('xsvc.diag.subject'))}`;

  return (
    <section id="importador-de-registro" className="py-20 lg:py-24 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className="relative rounded-3xl p-7 md:p-12 overflow-hidden"
          style={{
            background: 'linear-gradient(155deg, #0d1a27 0%, #152b40 46%, #10333f 100%)',
            border: `2px solid ${GOLD}`,
            boxShadow: '0 0 0 5px rgba(201,168,76,0.16), 0 24px 60px rgba(11,44,74,0.22)',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(760px 340px at 88% 0%, rgba(201,168,76,0.10), transparent 65%)' }}
          />

          <div className="relative">
            {/* Encabezado */}
            <div className="pl-5 md:pl-6 mb-10" style={{ borderLeft: `5px solid ${GOLD}` }}>
              <span className="block text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: GOLD_TEXT }}>
                {t('xsvc.label')}
              </span>
              <h2
                className="font-display font-bold text-white m-0"
                style={{ fontSize: 'clamp(30px, 4vw, 46px)', letterSpacing: '-0.025em', lineHeight: '1.08' }}
              >
                {t('xsvc.title')}
              </h2>
              <p className="text-sm mt-3 mb-0" style={{ color: 'rgba(255,255,255,0.55)' }}>{t('xsvc.meta')}</p>
            </div>

            {/* Por qué / responsabilidad */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
              <div>
                <h3 className="font-display text-xl font-bold text-white mb-3" style={{ letterSpacing: '-0.01em' }}>{t('xsvc.why.title')}</h3>
                <p className="leading-relaxed m-0" style={{ color: 'rgba(255,255,255,0.72)' }}>{t('xsvc.why.body')}</p>
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-white mb-3" style={{ letterSpacing: '-0.01em' }}>{t('xsvc.resp.title')}</h3>
                <p className="leading-relaxed m-0" style={{ color: 'rgba(255,255,255,0.72)' }}>{t('xsvc.resp.body')}</p>
              </div>
            </div>

            {/* Qué hacemos */}
            <h3 className="font-display text-xl font-bold text-white mb-5" style={{ letterSpacing: '-0.01em' }}>{t('xsvc.do.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
              {[1, 2].map((n) => (
                <div
                  key={n}
                  className="rounded-2xl p-6"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center font-display font-bold shrink-0"
                      style={{ background: GOLD, color: '#241c05' }}
                    >
                      {n}
                    </span>
                    <span className="font-bold text-white">{t(`xsvc.do.${n}.title`)}</span>
                  </div>
                  <p className="text-sm leading-relaxed m-0" style={{ color: 'rgba(255,255,255,0.70)' }}>{t(`xsvc.do.${n}.body`)}</p>
                </div>
              ))}
            </div>

            {/* Cómo trabajamos + diagnóstico + descargas */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-8">
                <div>
                  <h3 className="font-display text-xl font-bold text-white mb-3" style={{ letterSpacing: '-0.01em' }}>{t('xsvc.how.title')}</h3>
                  <p className="leading-relaxed m-0" style={{ color: 'rgba(255,255,255,0.72)' }}>{t('xsvc.how.body')}</p>
                </div>
                <div className="rounded-2xl p-6" style={{ background: 'rgba(201,168,76,0.10)', border: `1.5px solid ${GOLD}` }}>
                  <h3 className="font-display text-xl font-bold mb-2" style={{ color: GOLD_TEXT, letterSpacing: '-0.01em' }}>{t('xsvc.diag.title')}</h3>
                  <p className="leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.80)' }}>{t('xsvc.diag.body')}</p>
                  <a
                    href={mailto}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5"
                    style={{ background: GOLD, color: '#241c05' }}
                  >
                    {t('xsvc.diag.cta')}
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </a>
                  <span className="block text-sm mt-3" style={{ color: 'rgba(255,255,255,0.55)' }}>contact@mercaliacg.com</span>
                </div>
              </div>

              <div className="lg:col-span-5">
                <span className="block text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: GOLD_TEXT }}>
                  {t('xsvc.downloads')}
                </span>
                <div className="space-y-3">
                  <DownloadLink href={sheet} label={t('xsvc.dl.sheet')} />
                  <DownloadLink href={sheetAlt} label={t('xsvc.dl.sheetAlt')} />
                  <DownloadLink href={DOCS.catalog} label={t('xsvc.dl.catalog')} />
                </div>
              </div>
            </div>

            <p className="text-xs mt-10 mb-0" style={{ color: 'rgba(255,255,255,0.45)' }}>{t('xsvc.note')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
